import React from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { StatusBadge } from "@/components/ui/status-badge";
import { useTranslation } from "react-i18next";
import { useLeadsInfinite } from "@/pages/leads/hooks";
import { LeadStatus } from "@/pages/leads/types";
import { ComputedFields } from "@/services/api/types";
import { leadStatusColors } from "@/pages/leads/helpers";
import { getEnumTKey } from "@/utils/strings";

export type ConfirmIcpModalMode = "enable" | "disable";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: ConfirmIcpModalMode;
  onConfirm: () => void;
}

const TRANSLATION_KEY =
  "settings.leads.tabs.generalTab.leadWorkflow.confirmIcp.confirmIcpModal";

const CONFIRM_ICP_FILTER = { status: [LeadStatus.CONFIRM_ICP] };

const ConfirmIcpModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  mode,
  onConfirm
}) => {
  const { t } = useTranslation();

  const { total: confirmIcpLeadsCount } = useLeadsInfinite(
    CONFIRM_ICP_FILTER,
    undefined,
    undefined,
    1,
    undefined,
    undefined,
    false,
    isOpen && mode === "disable",
    ComputedFields.MINIMAL
  );

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onConfirm();
    setIsOpen(false);
  };

  const isDisableMode = mode === "disable";

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      data-test="confirm-icp-modal"
      contentClassname="w-[35rem]"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {isDisableMode
            ? t(`${TRANSLATION_KEY}.disable.title`)
            : t(`${TRANSLATION_KEY}.enable.title`)}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 p-4">
          {isDisableMode ? (
            <>
              {confirmIcpLeadsCount > 0 ? (
                <div className="flex flex-row items-center gap-2">
                  <Text className="whitespace-nowrap">
                    {t(`${TRANSLATION_KEY}.disable.leadsCount`, {
                      count: confirmIcpLeadsCount
                    })}
                  </Text>
                  <div className="w-fit">
                    <StatusBadge
                      statusColor={leadStatusColors[LeadStatus.CONFIRM_ICP]}
                    >
                      {t(
                        getEnumTKey(
                          "leads",
                          "statusValues",
                          LeadStatus,
                          LeadStatus.CONFIRM_ICP
                        )
                      )}
                    </StatusBadge>
                  </div>
                  <Text className="whitespace-nowrap">
                    {t(`${TRANSLATION_KEY}.disable.willMoveTo`)}
                  </Text>
                  <StatusBadge
                    statusColor={leadStatusColors[LeadStatus.IN_PROGRESS]}
                  >
                    {t(
                      getEnumTKey(
                        "leads",
                        "statusValues",
                        LeadStatus,
                        LeadStatus.IN_PROGRESS
                      )
                    )}
                  </StatusBadge>
                </div>
              ) : (
                <Text>{t(`${TRANSLATION_KEY}.disable.noLeads`)}</Text>
              )}
              <div className="flex flex-row items-center gap-2">
                <Text className="whitespace-nowrap">
                  {t(`${TRANSLATION_KEY}.disable.newLeadsWillGo`)}
                </Text>
                <div className="w-fit">
                  <StatusBadge
                    statusColor={leadStatusColors[LeadStatus.IN_PROGRESS]}
                  >
                    {t(
                      getEnumTKey(
                        "leads",
                        "statusValues",
                        LeadStatus,
                        LeadStatus.IN_PROGRESS
                      )
                    )}
                  </StatusBadge>
                </div>
                <Text className="whitespace-nowrap">
                  {t(`${TRANSLATION_KEY}.disable.status`)}
                </Text>
              </div>
            </>
          ) : (
            <div className="flex flex-row items-center gap-2">
              <Text className="whitespace-nowrap">
                {t(`${TRANSLATION_KEY}.disable.newLeadsWillGo`)}
              </Text>
              <div className="w-fit">
                <StatusBadge
                  statusColor={leadStatusColors[LeadStatus.CONFIRM_ICP]}
                >
                  {t(
                    getEnumTKey(
                      "leads",
                      "statusValues",
                      LeadStatus,
                      LeadStatus.CONFIRM_ICP
                    )
                  )}
                </StatusBadge>
              </div>
              <Text className="whitespace-nowrap">
                {t(`${TRANSLATION_KEY}.disable.status`)}
              </Text>
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleCancel}
            data-test="confirm-icp-modal-cancel"
          >
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirm} data-test="confirm-icp-modal-confirm">
            {isDisableMode
              ? t(`${TRANSLATION_KEY}.disable.submitButton`)
              : t(`${TRANSLATION_KEY}.enable.submitButton`)}
          </Button>
        </div>
      }
    />
  );
};

export default ConfirmIcpModal;
